/**
 * Quiz answers (AnswersCollector.cs / Answers.cs / Answer.cs), stored in
 * Firestore instead of the PHP backend:
 *
 *   projects/{projectId}/answers/{answerId}
 *     { projectId, userName, answers: [{slideId, answer, correct, points}],
 *       totalPoints, finished, createdAt, updatedAt }
 *
 * The document is created when the player enters a name and updated after
 * each question (so a half-played quiz already shows in the ranking, as in the
 * app). A copy is kept in localStorage so the same device can resume an
 * unfinished quiz or is kept from replaying one (`allowRepeat`).
 *
 * Demos (and any project played "locally") never touch Firestore.
 */
import type { Project } from "@makeit/schema";

export interface Answer {
  slideId: number;
  answer: string;
  correct: boolean;
  points: number;
}

export interface MyAnswers {
  id: string | null; // Firestore document id (null while local-only)
  projectId: string;
  userName: string;
  answers: Answer[];
  totalPoints: number;
  finished: boolean;
  /** Classroom student who played (so another student on the same device starts afresh). */
  studentId?: string;
  classroomId?: string;
}

export interface RankingEntry {
  id: string | null;
  userName: string;
  answers: Answer[];
  totalPoints: number;
  mine: boolean;
}

const KEY = (projectId: string) => `makeit:answers:${projectId}`;

export function loadLocalAnswers(projectId: string, studentId?: string | null): MyAnswers | null {
  try {
    const raw = localStorage.getItem(KEY(projectId));
    if (!raw) return null;
    const a = JSON.parse(raw) as MyAnswers;
    if (!a || a.projectId !== projectId || !Array.isArray(a.answers)) return null;
    if ((a.studentId ?? null) !== (studentId ?? null)) return null;
    return a;
  } catch {
    return null;
  }
}

export function clearLocalAnswers(projectId: string): void {
  try {
    localStorage.removeItem(KEY(projectId));
  } catch {
    /* ignore */
  }
}

function saveLocal(a: MyAnswers): void {
  try {
    localStorage.setItem(KEY(a.projectId), JSON.stringify(a));
  } catch {
    /* ignore */
  }
}

async function fs() {
  const [{ getFirestore, collection, addDoc, updateDoc, doc, getDocs, query, orderBy, limit, where, serverTimestamp }, { getApp }] = await Promise.all([
    import("firebase/firestore"),
    import("../firebase"),
  ]);
  return { db: getFirestore(getApp()), collection, addDoc, updateDoc, doc, getDocs, query, orderBy, limit, where, serverTimestamp };
}

/** AnswersCollector.SetName: starts a new answers record. */
export async function startAnswers(project: Project, userName: string, cloud: boolean, classroom?: { classroomId: string; studentId: string }): Promise<MyAnswers> {
  const mine: MyAnswers = { id: null, projectId: project.id, userName: userName.trim().slice(0, 40), answers: [], totalPoints: 0, finished: false, ...(classroom ? { studentId: classroom.studentId, classroomId: classroom.classroomId } : {}) };
  if (cloud) {
    try {
      const f = await fs();
      const ref = await f.addDoc(f.collection(f.db, "projects", project.id, "answers"), {
        projectId: project.id,
        userName: mine.userName,
        answers: [],
        totalPoints: 0,
        finished: false,
        createdAt: f.serverTimestamp(),
        updatedAt: f.serverTimestamp(),
        ...(classroom ? { classroomId: classroom.classroomId, studentId: classroom.studentId } : {}),
      });
      mine.id = ref.id;
    } catch (err) {
      console.warn("answers: could not create the record, keeping it local", err);
    }
  }
  saveLocal(mine);
  return mine;
}

/** AnswersCollector.AddAnswer (points are computed by the activity engine). */
export async function addAnswer(mine: MyAnswers, answer: Answer, finished: boolean): Promise<MyAnswers> {
  const next: MyAnswers = {
    ...mine,
    answers: [...mine.answers.filter((a) => a.slideId !== answer.slideId), answer],
    finished,
  };
  next.totalPoints = next.answers.reduce((s, a) => s + a.points, 0);
  saveLocal(next);
  if (next.id) {
    try {
      const f = await fs();
      await f.updateDoc(f.doc(f.db, "projects", next.projectId, "answers", next.id), {
        answers: next.answers,
        totalPoints: next.totalPoints,
        finished,
        updatedAt: f.serverTimestamp(),
      });
    } catch (err) {
      console.warn("answers: could not send the answer", err);
    }
  }
  return next;
}

export async function markFinished(mine: MyAnswers): Promise<MyAnswers> {
  if (mine.finished) return mine;
  const next = { ...mine, finished: true };
  saveLocal(next);
  if (next.id) {
    try {
      const f = await fs();
      await f.updateDoc(f.doc(f.db, "projects", next.projectId, "answers", next.id), { finished: true, updatedAt: f.serverTimestamp() });
    } catch (err) {
      console.warn("answers: could not mark as finished", err);
    }
  }
  return next;
}

/**
 * QuizzRanquing.ShowRanking: everyone's answers, totals recomputed over the
 * slides played so far (`slideIds`), mine always included, sorted by points.
 */
export async function fetchRanking(mine: MyAnswers, slideIds: number[], cloud: boolean): Promise<RankingEntry[]> {
  const played = new Set(slideIds);
  const total = (answers: Answer[]) => answers.reduce((s, a) => s + (played.has(a.slideId) ? a.points : 0), 0);
  const entries: RankingEntry[] = [{ id: mine.id, userName: mine.userName, answers: mine.answers, totalPoints: total(mine.answers), mine: true }];
  if (cloud) {
    try {
      const f = await fs();
      // A classroom student sees only the classroom; the public ranking leaves classroom records out.
      const col = f.collection(f.db, "projects", mine.projectId, "answers");
      const q = mine.classroomId ? f.query(col, f.where("classroomId", "==", mine.classroomId), f.limit(500)) : f.query(col, f.orderBy("totalPoints", "desc"), f.limit(300));
      const snap = await f.getDocs(q);
      snap.forEach((d) => {
        if (d.id === mine.id) return;
        const data = d.data() as { userName?: unknown; answers?: unknown; classroomId?: unknown };
        if (!mine.classroomId && data.classroomId) return;
        const answers = Array.isArray(data.answers) ? (data.answers as Answer[]) : [];
        entries.push({ id: d.id, userName: typeof data.userName === "string" ? data.userName : "?", answers, totalPoints: total(answers), mine: false });
      });
    } catch (err) {
      console.warn("answers: could not load the ranking", err);
    }
  }
  entries.sort((a, b) => b.totalPoints - a.totalPoints);
  return entries;
}
